import React, { useEffect, useRef } from "react";
import Highcharts from "highcharts";

import { Box, Typography } from "@material-ui/core";

import Column from "components/visuals/Column";
import useColourPalette from "components/visuals/useColourPalette";

import { useAppSelector } from "store";
import {
    selectIsLoading,
    selectHasErrors,
    selectNumberOfStoresPerCategory,
} from "modules/customer/insights/portfolioNew/areaHealthNew/areaHealthNewSlice";
import { selectComparator, selectStore } from "modules/customer/insights/portfolioNew/portfolioSlice";
import stringUtils from "utils/stringUtils";

const NumberOfStoresPerCategory: React.FC = () => {
    const isLoading = useAppSelector(selectIsLoading);
    const hasErrors = useAppSelector(selectHasErrors);
    const data = useAppSelector(selectNumberOfStoresPerCategory);
    const selectedStore = useAppSelector(selectStore);
    const comparator = useAppSelector(selectComparator);
    const comparatorName = comparator?.name;
    const colourPalette = useColourPalette();

    const chartRef = useRef<Highcharts.Chart | null>(null);
    const setChart = (chart: Highcharts.Chart) => {
        chartRef.current = chart;
    };

    const selectedStoreDrilldownSeries: Highcharts.SeriesOptionsType[] = data.map(countsBySuperCategory => ({
        type: "column",
        id: `selected-store-${countsBySuperCategory.placeSuperCategory}`,
        name: selectedStore?.name,
        data: countsBySuperCategory.storeCountByPlaceCategory.map(item => ({
            name: item.placeCategory.name,
            y: item.selectedStoreStoreCount
        }))
    }));

    const comparatorDrilldownSeries: Highcharts.SeriesOptionsType[] = data.map(countsBySuperCategory => ({
        type: "column",
        id: `comparator-${countsBySuperCategory.placeSuperCategory}`,
        name: comparatorName,
        data: countsBySuperCategory.storeCountByPlaceCategory.map(item => ({
            name: item.placeCategory.name,
            y: item.comparatorStoreCount
        }))
    }));

    const selectedStoreData = data.map(item => {
        return {
            y: item.selectedStoreTotalStoreCount,
            name: item.placeSuperCategory,
            drilldown: `selected-store-${item.placeSuperCategory}`
        };
    });

    const comparatorData = data.map(item => ({
        y: item.comparatorTotalStoreCount,
        name: item.placeSuperCategory,
        drilldown: `comparator-${item.placeSuperCategory}`
    }));

    const options = React.useMemo((): Highcharts.Options => ({
        xAxis: {
            type: "category"
        },
        yAxis: {
            title: {
                text: "Number of stores"
            }
        },
        plotOptions: {
            column: {
                dataLabels: {
                    enabled: false
                },
                custom: {
                    printMe: true
                }
            }
        },
        tooltip: {
            enabled: true,
            shared: true,
            headerFormat: ``,
            footerFormat: ``,
            formatter: function () {
                const points = this.points ?? [];

                let categoryArr: string[] = [];
                let categoryFormatArr: string[] = [];
                let valueFormatArr: string[] = [];
                let valuesArr: string[] = [];

                points.forEach(point => {
                    const series = point.series;
                    categoryArr.push(series?.name);
                    categoryFormatArr.push(`color:${series?.options?.color};font-weight:bold`);
                    valueFormatArr.push(`color:${series?.options?.color};font-weight:bold`);
                    valuesArr.push(String(point.y));
                });

                return `<table>${stringUtils.tooltipHTML(categoryArr, {
                    header: points[0].key,
                    values: (valuesArr),
                    categoryFormattingArr: categoryFormatArr,
                    valueFormattingArr: valueFormatArr
                })}</table>`;
            },
            useHTML: true
        },
        series: [{
            type: "column",
            name: selectedStore?.name,
            data: selectedStoreData,
            color: colourPalette.comparators[0]
        }, {
            type: "column",
            name: comparatorName,
            data: comparatorData,
            color: colourPalette.comparators[1]
        }],
        drilldown: {
            allowPointDrilldown: false,
            activeAxisLabelStyle: {
                fontWeight: 'normal'
            },
            series: selectedStoreDrilldownSeries.concat(...comparatorDrilldownSeries)
        }
    }), [selectedStoreData, comparatorData, selectedStoreDrilldownSeries, comparatorDrilldownSeries, colourPalette.comparators, comparatorName, selectedStore?.name]);

    // Drill up when data changes
    useEffect(() => {
        const chart = chartRef.current;
        if (chart) {
            chart.drillUp();
            if (chart.series) {
                if (!data || data.length === 0) {
                    chart.series[0].setData([]);
                    chart.series[1].setData([]);
                } else {
                    chart.series[0].setData(selectedStoreData);
                    chart.series[1].setData(comparatorData);
                }
            }
        }
    }, [data, selectedStoreData, comparatorData]);

    return (
        <Box data-cy="number-of-stores-per-category">
            <Typography variant="h6" component="div" gutterBottom>
                Number of stores per category within the retail zone of your {selectedStore?.name} store vs {comparatorName}
            </Typography>
            <Column
                loading={isLoading}
                error={hasErrors}
                options={options}
                setChart={setChart}
            />
        </Box>
    );
};

export default NumberOfStoresPerCategory;
